import { useState, useEffect, useRef, useCallback } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

type PickupLocationSelectorProps = {
  fromTitle: string
  fromAddress: string
  onLocationChange: (location: {
    fromTitle: string
    fromAddress: string
    position?: { lat: number; lng: number }
    keyword?: string
  }) => void
}

type PlaceSuggestion = {
  title: string
  address: string
  lat?: string
  lon?: string
  icon?: string
}

const POPULAR_PICKUP_POINTS: PlaceSuggestion[] = [
  {
    title: 'Salzburg Airport (SZG)',
    address: 'Innsbrucker Bundesstraße 95, 5020 Salzburg, Austria',
    lat: '47.7944',
    lon: '13.0033',
    icon: '✈️',
  },
  {
    title: 'Salzburg Central Station (Hauptbahnhof)',
    address: 'Südtiroler Platz 1, 5020 Salzburg, Austria',
    lat: '47.8130',
    lon: '13.0456',
    icon: '🚆',
  },
  {
    title: 'Salzburg Old Town (Altstadt)',
    address: 'Residenzplatz, 5020 Salzburg, Austria',
    lat: '47.7981',
    lon: '13.0464',
    icon: '🏰',
  },
  {
    title: 'Mirabell Palace & Gardens',
    address: 'Mirabellplatz 4, 5020 Salzburg, Austria',
    lat: '47.8055',
    lon: '13.0435',
    icon: '🏛️',
  },
  {
    title: 'Hotel Sacher Salzburg',
    address: 'Schwarzstraße 5-7, 5020 Salzburg, Austria',
    lat: '47.8020',
    lon: '13.0434',
    icon: '🏨',
  },
  {
    title: 'Mozart Residence (Makartplatz)',
    address: 'Makartplatz 8, 5020 Salzburg, Austria',
    lat: '47.8033',
    lon: '13.0436',
    icon: '🎵',
  },
]

// High-definition Google Maps style pin icon for Leaflet
const PIN_ICON_HTML = `
  <div style="position: relative; display: flex; flex-direction: column; align-items: center; transform: translate(-50%, -100%); cursor: grab;">
    <div style="
      background: linear-gradient(135deg, #EA4335, #D93025);
      width: 38px;
      height: 38px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 16px rgba(234, 67, 53, 0.45);
      border: 2.5px solid #FFFFFF;
    ">
      <div style="
        width: 14px;
        height: 14px;
        background: #FFFFFF;
        border-radius: 50%;
        transform: rotate(45deg);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="width: 6px; height: 6px; background: #EA4335; border-radius: 50%;"></div>
      </div>
    </div>
    <div style="
      width: 12px;
      height: 5px;
      background: rgba(0, 0, 0, 0.3);
      border-radius: 50%;
      margin-top: 3px;
      filter: blur(1.5px);
    "></div>
  </div>
`

export default function PickupLocationSelector({
  fromTitle,
  fromAddress,
  onLocationChange,
}: PickupLocationSelectorProps) {
  const [isEnabled, setIsEnabled] = useState(false)
  const originalLocationRef = useRef<{ title: string; address: string }>({
    title: fromTitle,
    address: fromAddress,
  })

  // When initial fromTitle/fromAddress changes while disabled, keep reference updated
  useEffect(() => {
    if (!isEnabled && fromTitle) {
      originalLocationRef.current = { title: fromTitle, address: fromAddress }
    }
  }, [fromTitle, fromAddress, isEnabled])

  const handleToggleEnabled = (checked: boolean) => {
    setIsEnabled(checked)
    if (!checked) {
      onLocationChange({
        fromTitle: originalLocationRef.current.title,
        fromAddress: originalLocationRef.current.address,
        keyword: originalLocationRef.current.title,
      })
      setSearchQuery(originalLocationRef.current.title)
    }
  }

  const [searchQuery, setSearchQuery] = useState(fromTitle || '')
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isDetectingGps, setIsDetectingGps] = useState(false)
  const [isGeocodingPin, setIsGeocodingPin] = useState(false)
  const [detectError, setDetectError] = useState<string | null>(null)
  const [pinCoords, setPinCoords] = useState<{ lat: number; lng: number }>({
    lat: 47.8095,
    lng: 13.055,
  })

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.Marker | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Current active query for Google Maps external link
  const activeMapQuery = fromAddress
    ? fromTitle && !fromAddress.toLowerCase().includes(fromTitle.toLowerCase())
      ? `${fromTitle}, ${fromAddress}`
      : fromAddress
    : fromTitle || 'Salzburg, Austria'

  // Reverse geocode given coordinates and commit to parent state
  const reverseGeocodeCoords = useCallback(
    async (lat: number, lng: number) => {
      setIsGeocodingPin(true)
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 4000)

        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
          { signal: controller.signal }
        )
        clearTimeout(timeoutId)

        if (res.ok) {
          const data = await res.json()
          const road =
            data.address?.road ||
            data.address?.pedestrian ||
            data.address?.suburb ||
            data.name ||
            'Selected Location'
          const houseNumber = data.address?.house_number ? ` ${data.address.house_number}` : ''
          const city =
            data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            data.address?.county ||
            'Salzburg'
          const postcode = data.address?.postcode ? `${data.address.postcode} ` : ''
          const country = data.address?.country || 'Austria'

          const title = data.name || `${road}${houseNumber}`.trim() || 'Pinned Location'
          const address = `${road}${houseNumber}, ${postcode}${city}, ${country}`.replace(/^,\s*/, '')

          setSearchQuery(title)
          onLocationChange({
            fromTitle: title,
            fromAddress: address,
            position: { lat, lng },
            keyword: title,
          })
          return
        }
      } catch (err) {
        console.error('Reverse geocode error:', err)
      } finally {
        setIsGeocodingPin(false)
      }

      // Fallback
      const title = 'Pinned Map Location'
      const address = `Coordinates: ${lat.toFixed(5)}, ${lng.toFixed(5)}`
      setSearchQuery(title)
      onLocationChange({
        fromTitle: title,
        fromAddress: address,
        position: { lat, lng },
        keyword: title,
      })
    },
    [onLocationChange]
  )

  // Update pin position and optionally reverse geocode
  const updatePinPosition = useCallback(
    (lat: number, lng: number, shouldGeocode = true, panMap = true) => {
      setPinCoords({ lat, lng })

      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng])
      }

      if (panMap && mapRef.current) {
        mapRef.current.panTo([lat, lng], { animate: true, duration: 0.8 })
      }

      if (shouldGeocode) {
        reverseGeocodeCoords(lat, lng)
      }
    },
    [reverseGeocodeCoords]
  )

  // Initialize Leaflet Map
  useEffect(() => {
    if (!isEnabled || !mapContainerRef.current || mapRef.current) return

    const initialLat = pinCoords.lat
    const initialLng = pinCoords.lng

    const map = L.map(mapContainerRef.current, {
      center: [initialLat, initialLng],
      zoom: 15,
      scrollWheelZoom: false,
      zoomControl: true,
    })

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map)

    const customIcon = L.divIcon({
      className: 'custom-pin-marker',
      html: PIN_ICON_HTML,
      iconSize: [0, 0],
      iconAnchor: [0, 0],
    })

    const marker = L.marker([initialLat, initialLng], {
      icon: customIcon,
      draggable: true,
      autoPan: true,
    }).addTo(map)

    // Event: Click on map to drop / move pin
    map.on('click', (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng
      updatePinPosition(lat, lng, true, true)
    })

    // Event: Drag end on marker
    marker.on('dragend', () => {
      const pos = marker.getLatLng()
      updatePinPosition(pos.lat, pos.lng, true, false)
    })

    mapRef.current = map
    markerRef.current = marker

    // Ensure map tiles render without grey glitch
    const timer = setTimeout(() => {
      map.invalidateSize()
    }, 250)

    const handleResize = () => map.invalidateSize()
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', handleResize)
      map.remove()
      mapRef.current = null
      markerRef.current = null
    }
  }, [isEnabled, pinCoords.lat, pinCoords.lng, updatePinPosition])

  // Try initial geocoding of fromTitle/fromAddress if present
  useEffect(() => {
    if (!isEnabled) return
    const initialQuery = fromAddress || fromTitle
    if (!initialQuery || initialQuery === 'Salzburg, Austria') return

    let isMounted = true
    const geocodeInitial = async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            initialQuery
          )}&limit=1`
        )
        if (res.ok) {
          const data = await res.json()
          if (data && data.length > 0 && isMounted) {
            const lat = parseFloat(data[0].lat)
            const lng = parseFloat(data[0].lon)
            if (!isNaN(lat) && !isNaN(lng)) {
              setPinCoords({ lat, lng })
              if (markerRef.current) markerRef.current.setLatLng([lat, lng])
              if (mapRef.current) mapRef.current.setView([lat, lng], 15)
            }
          }
        }
      } catch {
        // ignore initial geocoding error
      }
    }

    geocodeInitial()
    return () => {
      isMounted = false
    }
  }, [isEnabled])

  // Debounced search on Google Maps / Nominatim Places
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSuggestions([])
      setIsSearching(false)
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const query = searchQuery.trim()

        // 1. Local match against top Salzburg popular points
        const localMatches = POPULAR_PICKUP_POINTS.filter(
          (p) =>
            p.title.toLowerCase().includes(query.toLowerCase()) ||
            p.address.toLowerCase().includes(query.toLowerCase())
        )

        // 2. Fetch live OpenStreetMap / Google Geocode places
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            query
          )}&countrycodes=at,de&addressdetails=1&limit=6`
        )

        let remoteMatches: PlaceSuggestion[] = []
        if (res.ok) {
          const data = await res.json()
          remoteMatches = data.map((item: any) => {
            const name = item.name || item.address?.road || item.display_name.split(',')[0]
            return {
              title: name,
              address: item.display_name,
              lat: item.lat,
              lon: item.lon,
              icon:
                item.type === 'aerodrome'
                  ? '✈️'
                  : item.type === 'station'
                  ? '🚆'
                  : item.type === 'hotel'
                  ? '🏨'
                  : '📍',
            }
          })
        }

        // Combine unique suggestions
        const combined = [...localMatches]
        remoteMatches.forEach((rm) => {
          if (
            !combined.some(
              (c) =>
                c.title.toLowerCase() === rm.title.toLowerCase() ||
                c.address.toLowerCase() === rm.address.toLowerCase()
            )
          ) {
            combined.push(rm)
          }
        })

        setSuggestions(combined)
        setIsDropdownOpen(true)
      } catch (err) {
        console.error('Place search error:', err)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  // Handle selecting a place suggestion
  const handleSelectPlace = (place: PlaceSuggestion) => {
    setSearchQuery(place.title)
    setIsDropdownOpen(false)
    setDetectError(null)

    const lat = place.lat ? parseFloat(place.lat) : undefined
    const lng = place.lon ? parseFloat(place.lon) : undefined
    const pos = lat !== undefined && lng !== undefined && !isNaN(lat) && !isNaN(lng) ? { lat, lng } : undefined

    onLocationChange({
      fromTitle: place.title,
      fromAddress: place.address,
      position: pos,
      keyword: place.title,
    })

    if (place.lat && place.lon) {
      const lat = parseFloat(place.lat)
      const lng = parseFloat(place.lon)
      if (!isNaN(lat) && !isNaN(lng)) {
        setPinCoords({ lat, lng })
        if (markerRef.current) markerRef.current.setLatLng([lat, lng])
        if (mapRef.current) {
          mapRef.current.flyTo([lat, lng], 16, { duration: 1.2 })
        }
      }
    }
  }

  // Handle manual input text edit
  const handleManualInputCommit = (titleVal: string, addressVal?: string) => {
    const title = titleVal.trim() || 'Pickup Location'
    const address = addressVal?.trim() || `${titleVal}, Salzburg, Austria`
    onLocationChange({
      fromTitle: title,
      fromAddress: address,
      position: pinCoords,
      keyword: title,
    })
  }

  // Detect GPS Location
  const handleDetectCurrentLocation = () => {
    if (!navigator.geolocation) {
      setDetectError('Geolocation is not supported by your browser. Please search or drop a pin on the map.')
      return
    }

    setIsDetectingGps(true)
    setDetectError(null)

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords

        // Pan map and move marker
        setPinCoords({ lat: latitude, lng: longitude })
        if (markerRef.current) markerRef.current.setLatLng([latitude, longitude])
        if (mapRef.current) {
          mapRef.current.flyTo([latitude, longitude], 16, { duration: 1.2 })
        }

        try {
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 4000)

          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            { signal: controller.signal }
          )
          clearTimeout(timeoutId)

          if (res.ok) {
            const data = await res.json()
            const road =
              data.address?.road ||
              data.address?.pedestrian ||
              data.address?.suburb ||
              'Current Location'
            const houseNumber = data.address?.house_number ? ` ${data.address.house_number}` : ''
            const city =
              data.address?.city || data.address?.town || data.address?.village || 'Salzburg'
            const postcode = data.address?.postcode ? `${data.address.postcode} ` : ''

            const title = `My Current Location (${road}${houseNumber})`.trim()
            const address = `${postcode}${city}, Austria (GPS: ${latitude.toFixed(4)}, ${longitude.toFixed(4)})`

            setIsDetectingGps(false)
            setSearchQuery(title)
            onLocationChange({
              fromTitle: title,
              fromAddress: address,
              position: { lat: latitude, lng: longitude },
              keyword: title,
            })
            return
          }
        } catch {
          // Fallback if reverse geocode fails
        }

        const title = 'My GPS Location'
        const address = `Salzburg Region (GPS: ${latitude.toFixed(4)}, ${longitude.toFixed(4)})`
        setIsDetectingGps(false)
        setSearchQuery(title)
        onLocationChange({
          fromTitle: title,
          fromAddress: address,
          position: { lat: latitude, lng: longitude },
          keyword: title,
        })
      },
      (error) => {
        setIsDetectingGps(false)
        let msg = 'Unable to detect location. Please search on the map or click to drop a pin.'
        if (error.code === error.PERMISSION_DENIED) {
          msg = 'Location permission was denied. You can search your address or click anywhere on the map to drop a pin.'
        }
        setDetectError(msg)
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    )
  }

  // Recenter map on pin
  const handleRecenterPin = () => {
    if (mapRef.current && pinCoords) {
      mapRef.current.flyTo([pinCoords.lat, pinCoords.lng], 16, { duration: 0.8 })
    }
  }

  return (
    <div className="overflow-hidden rounded-[22px] bg-white shadow-[0_5px_18px_rgba(0,0,0,0.09)] border border-[#ECECEC] transition-all">
      {/* Header with Optional Toggle Checkbox */}
      <div
        className={`px-4 py-4 sm:px-7 sm:py-5 transition-colors ${
          isEnabled ? 'border-b border-[#EAEAEA] bg-white' : 'hover:bg-[#FCFCFC]'
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-3.5 cursor-pointer select-none">
            <div
              onClick={(e) => {
                e.preventDefault()
                handleToggleEnabled(!isEnabled)
              }}
              className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[5px] border transition-colors ${
                isEnabled
                  ? 'bg-[#9BC344] border-[#9BC344] text-white shadow-sm'
                  : 'border-[#777777] bg-white hover:border-[#9BC344]'
              }`}
            >
              {isEnabled && (
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => handleToggleEnabled(e.target.checked)}
              className="sr-only"
            />
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EAF6D4] text-[#65951E] shrink-0">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-host text-[18px] font-semibold text-black sm:text-[20px]">
                  Choose your current location
                </h2>
                <span className="rounded-full bg-[#F3F4F6] border border-[#E5E7EB] px-2.5 py-0.5 font-poppins text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">
                  Optional
                </span>
              </div>
              <p className="font-poppins text-[11px] text-[#777777] mt-0.5">
                {isEnabled
                  ? 'Search Google Maps, use your GPS, or drop a pin anywhere on the map'
                  : 'Check this box to set your exact live GPS location, hotel, or address for pickup'}
              </p>
            </div>
          </label>

          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF6D4] px-3 py-1 font-poppins text-[11px] font-semibold text-[#5B8817] border border-[#D5EAA8]">
              <span className="h-2 w-2 rounded-full bg-[#65951E] animate-pulse" />
              FREE PICKUP INCLUDED
            </span>
          </div>
        </div>
      </div>

      {/* Body: Single Unified View (only shown when enabled) */}
      {isEnabled && (
        <div className="p-4 sm:p-7 space-y-4">
        {/* Unified Search & GPS Control Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
          {/* Google Map Search Box */}
          <div ref={dropdownRef} className="relative flex-1">
            <div className="relative flex items-center">
              <div className="absolute left-3.5 flex items-center pointer-events-none text-[#EA4335]">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z" />
                </svg>
              </div>

              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  if (suggestions.length > 0) setIsDropdownOpen(true)
                }}
                placeholder="Search location, hotel, airport or address on Google Map..."
                className="h-[50px] w-full rounded-[12px] border border-[#D5D5D5] bg-white pl-11 pr-20 font-poppins text-[13px] text-black outline-none transition-colors focus:border-[#9BC344] focus:ring-2 focus:ring-[#9BC344]/20"
              />

              <div className="absolute right-3 flex items-center gap-2">
                {isSearching && (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#9BC344] border-t-transparent" />
                )}
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('')
                      setSuggestions([])
                    }}
                    className="text-xs text-gray-400 hover:text-black"
                  >
                    ✕
                  </button>
                )}
                <span className="hidden sm:inline-block rounded bg-[#F0F0F0] px-2 py-0.5 text-[10px] font-semibold text-[#666666]">
                  Maps
                </span>
              </div>
            </div>

            {/* Autocomplete Suggestions Dropdown */}
            {isDropdownOpen && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-[260px] overflow-y-auto rounded-[14px] border border-[#E0E0E0] bg-white p-2 shadow-[0_10px_25px_rgba(0,0,0,0.12)]">
                <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#888888]">
                  Matching Locations (Click to Select)
                </div>
                {suggestions.map((item, idx) => (
                  <button
                    key={`${item.title}-${idx}`}
                    type="button"
                    onClick={() => handleSelectPlace(item)}
                    className="flex w-full items-start gap-2.5 rounded-[10px] p-2.5 text-left transition-colors hover:bg-[#F6FBF0]"
                  >
                    <span className="text-[16px] shrink-0 mt-0.5">{item.icon || '📍'}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-host text-[13px] font-semibold text-black">
                        {item.title}
                      </p>
                      <p className="truncate font-poppins text-[11px] text-[#666666]">
                        {item.address}
                      </p>
                    </div>
                    <span className="shrink-0 rounded bg-[#EAF6D4] px-1.5 py-0.5 text-[9px] font-semibold text-[#5B8817]">
                      FREE
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Dedicated GPS Button */}
          <button
            type="button"
            onClick={handleDetectCurrentLocation}
            disabled={isDetectingGps}
            title="Detect and use your current GPS location"
            className="flex h-[50px] items-center justify-center gap-2 rounded-[12px] bg-[#9BC344] px-4 font-poppins text-[12px] font-semibold text-black transition-all hover:bg-[#8eb63a] active:scale-95 disabled:opacity-60 shrink-0 shadow-sm"
          >
            {isDetectingGps ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent" />
                <span>Locating GPS...</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" strokeWidth="2" />
                  <circle cx="12" cy="12" r="3" strokeWidth="2" fill="currentColor" />
                </svg>
                <span>Use My GPS</span>
              </>
            )}
          </button>
        </div>

        {/* GPS Error Alert (if any) */}
        {detectError && (
          <div className="flex items-center justify-between rounded-[10px] bg-[#FFF2F0] border border-[#FFCCC7] px-3.5 py-2.5 text-[12px] font-poppins text-[#CF1322]">
            <span>{detectError}</span>
            <button
              type="button"
              onClick={() => setDetectError(null)}
              className="text-xs font-bold text-[#CF1322] hover:opacity-75 ml-2"
            >
              ✕
            </button>
          </div>
        )}

        {/* Interactive Map with Pin Drop */}
        <div className="rounded-[16px] border border-[#E5E5E5] bg-[#FAFAFA] p-3 sm:p-4">
          {/* Map Top Bar */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
            <div className="flex items-center gap-2">
             
              <span className="font-poppins text-[11px] font-semibold uppercase tracking-wider text-[#444444]">
                Interactive Map & Pin Drop
              </span>
              {isGeocodingPin && (
                <span className="inline-flex items-center gap-1 rounded bg-[#EAF6D4] px-2 py-0.5 text-[10px] font-semibold text-[#5B8817]">
                  <span className="h-2.5 w-2.5 animate-spin rounded-full border border-[#5B8817] border-t-transparent" />
                  Finding address...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleRecenterPin}
                className="inline-flex items-center gap-1 font-poppins text-[11px] font-medium text-[#365E92] hover:underline"
              >
                <span>🎯</span> Recenter Pin
              </button>

              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activeMapQuery)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-poppins text-[11px] font-medium text-[#EA4335] hover:underline"
              >
                Open in Google Maps ↗
              </a>
            </div>
          </div>

          {/* Leaflet Map Canvas */}
          <div className="relative overflow-hidden rounded-[14px] border border-[#E0E0E0] bg-[#E8ECE9]">
            <div
              ref={mapContainerRef}
              className="h-[250px] w-full sm:h-[300px] z-0"
              style={{ minHeight: '250px' }}
            />

            {/* Instruction Banner Overlay */}
            <div className="absolute top-2.5 left-2.5 right-2.5 z-[1000] pointer-events-none flex justify-center">
              <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-black/75 px-3.5 py-1.5 backdrop-blur-md shadow-lg text-white font-poppins text-[11px]">
                <span>📍</span>
                <span>
                  <strong>Click map</strong> or <strong>drag pin</strong> to set exact pickup point
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Editable Pickup Details (Street / Landmark / Hotel) */}
        <div className="grid gap-3 sm:grid-cols-2 pt-1">
          <label className="min-h-[58px] rounded-[12px] bg-[#F7F7F7] px-4 py-2 border border-[#E0E0E0] focus-within:border-[#9BC344] focus-within:bg-white transition-all">
            <span className="block font-poppins text-[10px] font-medium text-[#777777]">
              Pickup Spot / Venue / Hotel Name
            </span>
            <input
              type="text"
              value={fromTitle}
              onChange={(e) => handleManualInputCommit(e.target.value, fromAddress)}
              placeholder="e.g. Hotel Sacher, Salzburg Airport Gate 1"
              className="mt-0.5 w-full bg-transparent font-poppins text-[13px] font-medium text-black outline-none"
            />
          </label>

          <label className="min-h-[58px] rounded-[12px] bg-[#F7F7F7] px-4 py-2 border border-[#E0E0E0] focus-within:border-[#9BC344] focus-within:bg-white transition-all">
            <span className="block font-poppins text-[10px] font-medium text-[#777777]">
              Street Address & House Number
            </span>
            <input
              type="text"
              value={fromAddress}
              onChange={(e) => handleManualInputCommit(fromTitle, e.target.value)}
              placeholder="e.g. Schwarzstraße 5-7, 5020 Salzburg"
              className="mt-0.5 w-full bg-transparent font-poppins text-[13px] font-medium text-black outline-none"
            />
          </label>
        </div>

        </div>
      )}
    </div>
  )
}
